import {
  isTrajectoryLoading,
  isTrajectoryVisible,
  TrajectoryItem,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import ActiveIndicatorChip from '../../components/ActiveIndicatorChip';
import { FitToViewModelCapabilityCoordinator } from '@trimble/web3d';
import { SelectionSyncCoordinator } from '@trimble/pane-sync';
import { t } from '@trimble/i18n';
import { SortableKeyOf, SortDirection } from '@trimble/ui-components/solidjs';
import { getTrbModelName } from '@trimble/web3d-providers';
import { handleNodeSelectionSync, setActiveAlignment } from './tree-helpers';
import { createMemo, JSX } from 'solid-js';
import {
  SelectionTree,
  SelectionTreeProps,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import {
  FilterHandlerRecord,
  ProjectDataFilterMode,
} from './project-data.types';
import { useLogger } from '@trimble/solidjs-utils';
import { LoadState } from '@trimble/data-loading';
import {
  buildTrajectoryNodes,
  TRAJECTORY_ROOT_NODE_ID,
} from './project-data-tree-nodes';

export interface TrajectorySelectionTreeContainerProps {
  nodes?: TreeNodeDefinition<TrajectoryItem>[];
  childrenLoadStatus?: SelectionTreeProps<TrajectoryItem>['childrenLoadStatus'];
  defaultExpandIds?: string[];
  filterText?: string;
  sortBy: SortableKeyOf<TreeNodeDefinition<TrajectoryItem>>;
  sortDirection: SortDirection;
  filterMode?: ProjectDataFilterMode;
}

const TrajectorySelectionTreeContainer = (
  props: TrajectorySelectionTreeContainerProps
): JSX.Element => {
  const { activeItems, dataState, dataLoadingService } = useDataContext();
  const { capabilityRegistry } = useCapabilityRegistryContext();

  const logger = useLogger().getLogger('TrajectorySelectionTreeContainer');

  const fitToViewCoordinator = capabilityRegistry.get(
    FitToViewModelCapabilityCoordinator
  );
  const selectionSyncCoordinator = capabilityRegistry.get(
    SelectionSyncCoordinator
  );

  const activeAlignmentId = createMemo<string | undefined>(
    () => activeItems.alignmentId ?? undefined
  );

  const isTrajectoryNodeLoading = (nodeId: string): boolean => {
    return nodeId === TRAJECTORY_ROOT_NODE_ID
      ? false
      : isTrajectoryLoading(dataState, nodeId);
  };

  const isTrajectoryNodeVisible = (nodeId: string): boolean => {
    return nodeId === TRAJECTORY_ROOT_NODE_ID
      ? false
      : isTrajectoryVisible(dataState, nodeId);
  };

  const isNodeSelected = (nodeId: string): boolean => {
    return (
      selectionSyncCoordinator()
        ?.selectedModelIds()
        .has(getTrbModelName(nodeId)) ?? false
    );
  };

  const handleNodeSelect = (
    nodeId: string,
    node: TreeNodeDefinition<TrajectoryItem>,
    e: MouseEvent
  ): void => {
    void handleNodeSelectionSync(
      getTrbModelName(nodeId),
      node,
      e,
      isTrajectoryNodeVisible,
      selectionSyncCoordinator()
    ).catch((error) => logger.error('Error selecting node', node, error));
  };

  const baseTreeNodes = createMemo((): TreeNodeDefinition<TrajectoryItem>[] => {
    if (props.nodes !== undefined) return props.nodes;

    const trajectory = dataState.trajectory;
    return trajectory.state === LoadState.LoadComplete
      ? buildTrajectoryNodes(Object.values(trajectory.items))
      : [];
  });

  const selectionTreeNodes = createMemo(
    (): TreeNodeDefinition<TrajectoryItem>[] =>
      baseTreeNodes().map((node) => {
        const item = node.data;
        if (!item) return node;
        const activeId = activeAlignmentId();
        const trajectoryLoaded =
          isTrajectoryVisible(dataState, item.id) &&
          !isTrajectoryLoading(dataState, item.id);
        const trajectoryActive = activeId != null && item.id === activeId;

        return {
          ...node,
          id: item.id,
          label: item.name,
          data: item,
          toggleTooltip: t(
            'mobileMapping.projectData.toggleTrajectoryVisibility'
          ),
          contextMenuOptions: [
            {
              text: t('siteVisualization.sidePanelLayout.fitView'),
              onClick: () => {
                void fitToViewCoordinator()?.fitToView(
                  getTrbModelName(item.id)
                );
              },
              disabled: () => !isTrajectoryVisible(dataState, item.id),
            },
            {
              text: t('mobileMapping.missions.setAsActive'),
              onClick: () => {
                setActiveAlignment(capabilityRegistry, item.id);
              },
              disabled: () => !isTrajectoryVisible(dataState, item.id),
            },
          ],
          visibleContent:
            trajectoryLoaded && trajectoryActive
              ? () => (
                  <ActiveIndicatorChip
                    icon="tc-icon-path-navigation"
                    tooltip={t('mobileMapping.missions.activeAlignmentTooltip')}
                    testId="active-alignment-tree-icon"
                  />
                )
              : undefined,
        } satisfies TreeNodeDefinition<TrajectoryItem>;
      })
  );

  const toggleTrajectoryVisibility = (nodeId: string): void => {
    if (isTrajectoryNodeLoading(nodeId) || isTrajectoryNodeVisible(nodeId)) {
      dataLoadingService.unload('trajectory', nodeId);
    } else {
      dataLoadingService.load('trajectory', nodeId);
    }
  };

  const filterHandlers: FilterHandlerRecord<TrajectoryItem> = {
    all: { filter: () => true },
    active: {
      filter: (node) =>
        node.id !== TRAJECTORY_ROOT_NODE_ID && isTrajectoryNodeVisible(node.id),
    },
    archived: { filter: () => false },
  };

  return (
    <div data-testid="trajectory-selection-tree">
      <SelectionTree
        multiSelect={false}
        childrenLoadStatus={
          props.childrenLoadStatus ??
          (dataState.trajectory.state === LoadState.LoadComplete
            ? 'success'
            : 'pending')
        }
        nodes={selectionTreeNodes()}
        sortBy={props.sortBy}
        sortDirection={props.sortDirection}
        filterText={props.filterText}
        filterMode={props.filterMode}
        filterHandlers={filterHandlers}
        defaultExpandIds={props.defaultExpandIds}
        isNodeLoading={isTrajectoryNodeLoading}
        isNodeVisible={isTrajectoryNodeVisible}
        onVisibilityToggle={toggleTrajectoryVisibility}
        isNodeSelected={isNodeSelected}
        onNodeSelect={handleNodeSelect}
      />
    </div>
  );
};

export default TrajectorySelectionTreeContainer;
